import { useState, useEffect, useCallback, useRef } from 'react';
import { countQuery, countDistinctQuery } from '../services/salesforce/client';
import { soqlDateFilter, TimePeriod } from '../utils/timePeriods';
import { EXCLUDE_DEACTIVATED_FIRMS } from '../constants';

export interface FunnelStage {
  label: string;
  count: number;
  /** Conversion rate from the previous stage, as a percent string (e.g. "42.0%"). Null for the first stage. */
  conversionFromPrev: string | null;
}

export interface UseFunnelResult {
  stages: FunnelStage[];
  demos: number;
  accounts: number;
  firstPlans: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const DEMO_PREDICATE =
  "Subject LIKE '%Demo%' AND Calendly__InviteeUuid__c != null AND (NOT Subject LIKE '%Canceled%')";

function rate(numerator: number, denominator: number): string | null {
  if (denominator <= 0) return null;
  return ((numerator / denominator) * 100).toFixed(1) + '%';
}

/**
 * Top-of-funnel counts for the Sales dashboard: Demos Booked → Accounts Created
 * → First Plans Done, filterable by rep (Owner) and time period.
 *
 * Each stage is counted independently by its own date field within the period,
 * so the funnel is directional rather than a strict cohort. Per-rep account and
 * plan counts key off Account.OwnerId, which may differ from the AE who ran the
 * demo — counts are most reliable at the "All AEs" roll-up.
 */
export function useFunnel(aeId: string | null, period: TimePeriod): UseFunnelResult {
  const [demos, setDemos] = useState(0);
  const [accounts, setAccounts] = useState(0);
  const [firstPlans, setFirstPlans] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [forceRefresh, setForceRefresh] = useState(0);

  const refetch = useCallback(() => setForceRefresh((t) => t + 1), []);

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;

    const ownerDemo = aeId ? ` AND OwnerId = '${aeId}'` : '';
    // Firm counts filter by the sales-rep owner via the Account lookup
    // (Encore_Firm__c.OwnerId is the sync/integration user, not the rep).
    const ownerFirm = aeId ? ` AND Account__r.OwnerId = '${aeId}'` : '';

    const demoSoql = `SELECT COUNT() FROM Event WHERE ${DEMO_PREDICATE} AND ${soqlDateFilter(
      'ActivityDate',
      period
    )}${ownerDemo}`;

    // Distinct firms (Encore_Firm__c is not 1:1 — seat/sync-dupe rows), counted
    // on the synced firm-created / first-plan dates.
    const accountsSoql = `SELECT COUNT_DISTINCT(EEP_Firm_ID__c) total FROM Encore_Firm__c WHERE EEP_Firm_ID__c != null${EXCLUDE_DEACTIVATED_FIRMS} AND ${soqlDateFilter(
      'EEP_Firm_Created_Date__c',
      period
    )}${ownerFirm}`;

    const firstPlanSoql = `SELECT COUNT_DISTINCT(EEP_Firm_ID__c) total FROM Encore_Firm__c WHERE EEP_Firm_ID__c != null${EXCLUDE_DEACTIVATED_FIRMS} AND First_Plan_Date__c != null AND ${soqlDateFilter(
      'First_Plan_Date__c',
      period
    )}${ownerFirm}`;

    async function fetchFunnel() {
      setLoading(true);
      setError(null);
      try {
        const [d, a, f] = await Promise.all([
          countQuery(demoSoql, controller.signal),
          countDistinctQuery(accountsSoql, controller.signal),
          countDistinctQuery(firstPlanSoql, controller.signal),
        ]);
        if (!cancelled) {
          setDemos(d);
          setAccounts(a);
          setFirstPlans(f);
        }
      } catch (err: unknown) {
        if (!cancelled && !(err instanceof DOMException && err.name === 'AbortError')) {
          setError(err instanceof Error ? err.message : 'Failed to load funnel');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchFunnel();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [aeId, period, forceRefresh]);

  const stages: FunnelStage[] = [
    { label: 'Demos Booked', count: demos, conversionFromPrev: null },
    { label: 'Accounts Created', count: accounts, conversionFromPrev: rate(accounts, demos) },
    { label: 'First Plans Done', count: firstPlans, conversionFromPrev: rate(firstPlans, accounts) },
  ];

  return { stages, demos, accounts, firstPlans, loading, error, refetch };
}
